import { useMemo, useState } from "react";
import { compararTexto, type DirecaoOrdenacao } from "@/lib/consultas";

type Valor = string | number | boolean | Date | null | undefined;

interface UseOrdenacaoLocalResult<T, K extends string> {
  itensOrdenados: T[];
  ordenarPor: K;
  direcao: DirecaoOrdenacao;
  /** Passe direto para `SortableTableHead.onOrdenar`. */
  onOrdenar: (campo: K) => void;
}

/**
 * Ordenação no navegador para listas PEQUENAS já inteiras em memória (cadastros
 * auxiliares, abas de detalhe). Lista paginada no servidor ordena no servidor.
 *
 * `chaves` mapeia o campo do cabeçalho para o valor comparado. Texto compara em
 * pt-BR sem diferenciar acento/maiúscula; nulos vão sempre para o fim.
 */
export function useOrdenacaoLocal<T, K extends string>(
  itens: T[],
  chaves: Record<K, (item: T) => Valor>,
  inicial: { ordenarPor: K; direcao?: DirecaoOrdenacao },
): UseOrdenacaoLocalResult<T, K> {
  const [ordenarPor, setOrdenarPor] = useState<K>(inicial.ordenarPor);
  const [direcao, setDirecao] = useState<DirecaoOrdenacao>(inicial.direcao ?? "asc");

  const itensOrdenados = useMemo(() => {
    const valor = chaves[ordenarPor];
    if (!valor) return itens;
    const sinal = direcao === "asc" ? 1 : -1;
    return [...itens].sort((a, b) => {
      const va = valor(a), vb = valor(b);
      const na = va === null || va === undefined || va === "";
      const nb = vb === null || vb === undefined || vb === "";
      if (na || nb) return na === nb ? 0 : na ? 1 : -1;
      if (typeof va === "string" && typeof vb === "string") return sinal * compararTexto(va, vb);
      const xa = va instanceof Date ? va.getTime() : Number(va);
      const xb = vb instanceof Date ? vb.getTime() : Number(vb);
      return sinal * (xa - xb);
    });
    // `chaves` costuma ser literal recriado a cada render: depende só do campo/direção/itens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itens, ordenarPor, direcao]);

  const onOrdenar = (campo: K) => {
    if (campo === ordenarPor) setDirecao((d) => (d === "asc" ? "desc" : "asc"));
    else { setOrdenarPor(campo); setDirecao("asc"); }
  };

  return { itensOrdenados, ordenarPor, direcao, onOrdenar };
}
